import { useEffect, useRef } from 'react';

const topFeatures = [
  {
    icon: 'ri-filter-3-line',
    title: 'Funnel Yapı',
    description: 'Ziyaretçiyi müşteriye dönüştüren akıllı satış huni sistemi. Her adım stratejik olarak optimize edilmiş.',
    color: 'text-gold',
    bg: 'bg-gold/10',
    border: 'border-gold/20',
  },
  {
    icon: 'ri-whatsapp-line',
    title: 'Anlık İletişim',
    description: 'WhatsApp ve akıllı formlar ile potansiyel müşteriler anında size ulaşır. Hiçbir fırsat kaçmaz.',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
    border: 'border-green-400/20',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Kampanya Sayfaları',
    description: 'Her reklam için özel landing page. Reklam bütçeniz maksimum verim sağlar, boşa gitmiyor.',
    color: 'text-neon',
    bg: 'bg-neon/10',
    border: 'border-neon/20',
  },
];

const bottomFeatures = [
  {
    icon: 'ri-palette-line',
    title: 'Premium Tasarım',
    description: 'Profesyonel, güvenilir ve modern görünüm. Ziyaretçi ilk saniyede etkileniyor, rakiplerinizden sıyrılıyorsunuz.',
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/20',
  },
  {
    icon: 'ri-rocket-2-line',
    title: 'Hızlı Kurulum',
    description: 'Saatler içinde sistem hazır ve yayında. Günlerce beklemenize, teknik süreçlerle uğraşmanıza gerek yok.',
    color: 'text-orange-400',
    bg: 'bg-orange-400/10',
    border: 'border-orange-400/20',
  },
];

export default function FeaturesSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.feat-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0) scale(1)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const FeatureCard = ({ feat }: { feat: typeof topFeatures[0] }) => (
    <div
      className={`feat-card glass rounded-2xl p-7 border ${feat.border} card-hover relative overflow-hidden group h-full`}
      style={{ opacity: 0, transform: 'translateY(20px) scale(0.97)', transition: 'all 0.4s ease' }}
    >
      <div className={`absolute inset-0 ${feat.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`} />
      <div className="relative z-10">
        <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${feat.bg} border ${feat.border} mb-5 group-hover:scale-110 transition-transform duration-300`}>
          <i className={`${feat.icon} text-2xl ${feat.color}`} />
        </div>
        <h3 className="text-xl font-extrabold text-white mb-3">{feat.title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{feat.description}</p>
      </div>
      <div className={`absolute bottom-0 right-0 w-20 h-20 ${feat.bg} rounded-tl-full opacity-25`} />
    </div>
  );

  return (
    <section
      id="features"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0a0a0f 0%, #0d0d18 100%)' }}
    >
      <div className="absolute top-0 left-0 right-0 section-divider" />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'radial-gradient(circle at 50% 30%, #f0b429, transparent 60%)' }}
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8" ref={ref}>
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 px-4 py-2 rounded-full mb-5">
            <i className="ri-star-line text-gold text-sm" />
            <span className="text-gold text-sm font-semibold">Özellikler</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Platform{' '}
            <span className="text-gold-gradient">Özellikleri</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Her özellik, daha fazla müşteri getirmek için tasarlanmıştır.
          </p>
        </div>

        {/* ROW 1 — 3 eşit kart */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
          {topFeatures.map((feat) => (
            <FeatureCard key={feat.title} feat={feat} />
          ))}
        </div>

        {/* ROW 2 — 2 kart ortalı */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {bottomFeatures.map((feat) => (
            <FeatureCard key={feat.title} feat={feat} />
          ))}
        </div>
      </div>
    </section>
  );
}
